import React, { ComponentType } from 'react'

type ModuleWithDefault<P = unknown> = { default: ComponentType<P> }

function lazyOnce<T>(importer: () => Promise<T>, opts: { retries?: number }) {
  const maxRetries = Math.max(0, opts?.retries ?? 0)

  const tryImport = (attempt = 0): Promise<T> =>
    importer()
      .then((m) => {
        // Some bundlers can return undefined on bad chunks; treat as failure.
        if (m === undefined) throw new Error('ChunkLoadError: undefined module')
        return m
      })
      .catch((err) => {
        // If we haven’t refreshed this tab yet, do it once to bust stale caches.
        const key = 'lazy-once-refreshed'
        const didRefresh = sessionStorage.getItem(key) === 'true'

        if (!didRefresh) {
          sessionStorage.setItem(key, 'true')
          // Reload the whole document to fetch the latest manifest & chunks.
          window.location.reload()
          // Return a never-resolving promise to stop React.lazy from continuing.
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          return new Promise<T>(() => {})
        }

        // Post-refresh, optionally retry import to cover transient network hiccups.
        if (attempt < maxRetries) return tryImport(attempt + 1)

        // Bubble the error to your ErrorBoundary/Suspense fallback.
        throw err
      })

  return tryImport()
}

export function lazySafe<P = unknown>(
  importer: () => Promise<ModuleWithDefault<P>>
): React.LazyExoticComponent<ComponentType<P>> {
  return React.lazy(() =>
    lazyOnce(importer, { retries: 1 })
  ) as React.LazyExoticComponent<ComponentType<P>>
}
